import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Spin, Tabs, Typography, message } from 'antd';
import api from '../../services/api';
import type { FilingControl, ProbateFilingSchema } from './probateFilingCatalog';

type Answers = Record<string, unknown>;
type Person = { value: string; label: string };

const personName = (p: any) => [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') || p.email || 'Unnamed person';

/** Counts of the slots this software deliberately leaves blank. */
function executionSummary(schema: ProbateFilingSchema) {
  const count = (kind: string) => schema.mappings.filter(m => m.execution === kind).length;
  const parts = [
    [count('signature'), 'signature or signing-date'],
    [count('notary'), 'notary'],
    [count('court'), 'court or clerk'],
  ].filter(([n]) => n) as [number, string][];
  return parts.map(([n, label]) => `${n} ${label}`).join(', ');
}

/** Form.Item passes value and onChange to its child; forward them to the input. */
function Control({ c, people, ...field }: { c: FilingControl; people: Person[]; [prop: string]: any }) {
  if (c.type === 'participant') return <Select allowClear showSearch optionFilterProp="label" options={people} {...field} />;
  if (c.type === 'select') return <Select allowClear showSearch optionFilterProp="label" options={c.options} {...field} />;
  if (c.type === 'number') return <InputNumber min={0} max={c.maxLength ? 10 ** c.maxLength - 1 : undefined} style={{ width: '100%' }} aria-label={c.label} {...field} />;
  if (c.type === 'date') return <Input type="date" aria-label={c.label} {...field} />;
  if (c.type === 'textarea') return <Input.TextArea rows={3} maxLength={c.maxLength} showCount={!!c.maxLength} aria-label={c.label} {...field} />;
  return <Input maxLength={c.maxLength} showCount={!!c.maxLength && c.maxLength < 40} aria-label={c.label} {...field} />;
}

/**
 * Filing details for one court document: only the decisions and selections
 * that belong to this filing. Names, addresses, SSNs, dates and amounts come
 * from the estate's shared records and are edited there.
 */
export default function DocumentAnswersModal({ open, onClose, onSaved, estateId, documentUid, title }: {
  open: boolean; onClose: () => void; onSaved: () => void; estateId: string; documentUid: string; title: string;
}) {
  const [form] = Form.useForm();
  const [schema, setSchema] = useState<ProbateFilingSchema | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stored, setStored] = useState<Answers>({});
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState<string>();

  useEffect(() => {
    if (!open || !estateId || !documentUid) return;
    let cancelled = false;
    setLoading(true); setError(''); setSchema(null); setStored({}); form.resetFields();
    Promise.all([
      api.get(`/probatedocs/${documentUid}/filing-schema`),
      api.get(`/estates/${estateId}/documents/${documentUid}/answers`),
      api.get(`/estates/${estateId}`),
    ]).then(([schemaResponse, answerResponse, estateResponse]) => {
      if (cancelled) return;
      setSchema(schemaResponse.data || null);
      const values = answerResponse.data.values || {};
      setStored(values); form.setFieldsValue(values);
      const estate = estateResponse.data;
      const persons = [estate.estateOwner, ...(estate.benefactors || []), ...(estate.participants || []), ...(estate.executor || []), ...(estate.lawyers || [])].filter(Boolean);
      setPeople(Array.from(new Map<string, Person>(persons.map((p: any) => [p.uid, { value: p.uid, label: personName(p) }])).values()));
    }).catch(() => { if (!cancelled) setError('Could not load filing details. Please retry.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, estateId, documentUid, form]);

  const save = async () => {
    try { await form.validateFields(); } catch (e: any) {
      const key = e.errorFields?.[0]?.name?.[0];
      setActiveSection(schema?.controls.find(c => c.key === key)?.section);
      return;
    }
    setSaving(true);
    try {
      const values: Answers = { ...stored, ...form.getFieldsValue(true) };
      for (const c of schema?.controls ?? []) {
        if (c.type === 'participants' && Array.isArray(values[c.key])) {
          // Drop empty rows; keep only the fields the server accepts.
          values[c.key] = (values[c.key] as any[]).filter(r => r?.uid).map(r => ({ uid: r.uid, ...(r.note ? { note: r.note } : {}) }));
        }
      }
      Object.keys(values).forEach(key => { if (values[key] === undefined) values[key] = null; });
      await api.put(`/estates/${estateId}/documents/${documentUid}/answers`, { values });
      message.success('Filing details saved'); onSaved(); onClose();
    } catch (e: any) { message.error(e?.response?.data?.message || 'Could not save filing details'); }
    finally { setSaving(false); }
  };

  const sections = Array.from(new Set(schema?.controls.map(c => c.section) || []));
  const leftBlank = schema ? executionSummary(schema) : '';
  return <Modal open={open} title={`Filing details: ${schema?.title || title}`} onCancel={onClose} width={820} styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    footer={<Button type="primary" loading={saving} disabled={loading || !!error || !schema?.controls.length} onClick={save}>Save filing details</Button>}>
    {loading ? <Spin /> : error ? <Alert type="error" message={error} /> : !schema ?
      <Alert type="warning" showIcon message="This document has no reviewed filing catalogue" description="Its fields fill only from mapped shared records. Ask for the document to be reviewed before relying on it for a filing." /> : <>
      <details style={{ marginBottom: 16 }}><summary>Shared records and signing guidance</summary>
      <Alert type="info" showIcon style={{ marginTop: 8 }}
        message="Shared details come from your estate records"
        description={<>
          Names, addresses, SSNs, dates, the court county and amounts are taken from Estate details, Participants, the Family Tree and Assets and Liabilities. Open the relevant records in a separate tab to keep these filing answers in place.
          <Space wrap style={{ display: 'flex', marginTop: 8 }}>
            <a href={`/estate/${estateId}`} target="_blank" rel="noreferrer">Estate details (new tab)</a>
            <a href="/participants" target="_blank" rel="noreferrer">Participants (new tab)</a>
            <a href="/assets" target="_blank" rel="noreferrer">Assets (new tab)</a>
            <a href="/liabilities" target="_blank" rel="noreferrer">Liabilities (new tab)</a>
          </Space>
          {leftBlank && <> This software leaves the {leftBlank} slots blank: they are completed when the document is signed, notarized or filed.</>}
        </>} /></details>
      {!schema.controls.length && <Typography.Paragraph>This document has no document-specific questions.</Typography.Paragraph>}
      <Form form={form} layout="vertical">
        {<Tabs animated={false} activeKey={activeSection || sections[0]} onChange={setActiveSection} items={sections.map(section => ({ key: section, label: section, forceRender: true, children: <section aria-label={section}><Typography.Title level={5}>{section}</Typography.Title>
          {schema.controls.filter(c => c.section === section).map(c => c.type === 'participants'
            ? <Form.Item key={c.key} label={c.label} extra={c.help}>
              <Form.List name={c.key}>{(rows, { add, remove }) => <>
                {rows.map(({ key, name }) => <Space key={key} align="start" wrap style={{ display: 'flex', marginBottom: 8 }}>
                  <Form.Item name={[name, 'uid']} rules={[{ required: true, message: 'Choose a person or remove the row' }]} style={{ width: 260, maxWidth: '100%', marginBottom: 0 }}>
                    <Select showSearch optionFilterProp="label" options={people} placeholder={`Row ${name + 1}: person`} aria-label={`${c.label}: row ${name + 1}`} />
                  </Form.Item>
                  {c.note && <Form.Item name={[name, 'note']} style={{ width: 220, maxWidth: '100%', marginBottom: 0 }}>
                    <Input placeholder={c.note} aria-label={`${c.label}: row ${name + 1}: ${c.note}`} />
                  </Form.Item>}
                  <Button onClick={() => remove(name)}>Remove</Button>
                </Space>)}
                <Button onClick={() => add({})} disabled={rows.length >= (c.max ?? 0)}>
                  Add person{c.max ? ` (${rows.length} of ${c.max} rows on the form)` : ''}
                </Button>
              </>}</Form.List>
            </Form.Item>
            : <Form.Item key={c.key} name={c.key} label={c.label} extra={c.help}><Control c={c} people={people} /></Form.Item>)}
        </section> }))} />}
      </Form>
    </>}
  </Modal>;
}
